/**
 * Cabinet update controller (PWA).
 *
 * The shell precaches itself, so a deploy doesn't reach a player until the
 * new service worker takes over AND the page reloads. The old autoUpdate
 * registration swapped workers silently and left the running shell stale —
 * new cartridges didn't appear without a hard refresh. Now the SW waits
 * (registerType 'prompt') and we surface a marquee-styled toast; its button
 * applies the update and reloads programmatically, which works identically
 * in an installed PWA window where there is no browser refresh button.
 */

import { registerSW } from 'virtual:pwa-register';
import { track } from './analytics.js';

// The cabinet sits open in attract mode for hours; deploys land mid-session.
// Poll for a new build on an interval and whenever the tab regains focus.
const UPDATE_CHECK_INTERVAL_MS = 15 * 60 * 1000;

export function attachUpdateController(): void {
  const toast = document.getElementById('update-toast');
  const reloadBtn = document.getElementById('update-toast-reload');
  if (!(toast instanceof HTMLElement) || !(reloadBtn instanceof HTMLButtonElement)) {
    console.warn('[arcadia] update toast: DOM nodes missing — skipping');
    return;
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      toast.hidden = false;
      track('update_toast_shown');
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setInterval(() => void registration.update(), UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void registration.update();
      });
    },
  });

  reloadBtn.addEventListener('click', () => {
    track('update_toast_accepted');
    reloadBtn.disabled = true;
    reloadBtn.textContent = 'REBOOTING…';
    // Activates the waiting SW and reloads the window — no browser chrome
    // required, so installed-PWA players get the new build in one tap.
    void updateSW(true);
  });
}
